import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import ClipLoader from "react-spinners/ClipLoader";

import Layout from "../reusable-components/Layout";
import RagChatView from "../reusable-components/RagChatView";
import { sanitizeText } from "../utilities/sanitizeText";

function Rag() {
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [responseData, setResponsedata] = useState([]);
  const [response, setResponse] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [isUploaded, setUploaded] = useState(false);
  const [file, setFile] = useState("");

  //   const api_url = "http://localhost:4000";
  //   const rag_api_url = "http://127.0.0.1:8000";
  const rag_api_url = "https://rag-ai-r2gl.onrender.com";
  const api_url = "https://bere-plus.onrender.com";

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowedExtensions = [".pdf", ".doc", ".docx", ".txt"];
    const allowedMimes = [
      "application/pdf",
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "text/plain",
    ];

    const fileName = selectedFile.name.toLowerCase();
    const isValid =
      allowedExtensions.some((ext) => fileName.endsWith(ext)) &&
      allowedMimes.includes(selectedFile.type);

    if (isValid) {
      setFile(selectedFile);
    } else {
      alert("Please upload a PDF, DOC, DOCX, or TXT file.");
      e.target.value = null;
    }
  };

  async function sendQuery() {
    setSending(true);

    try {
      const key = "rag_prompts";
      const ragPrompts = JSON.parse(sessionStorage.getItem("rag_prompts"));
      const normalizeInput = sanitizeText(textInput);

      if (ragPrompts?.length > 0) {
        sessionStorage.setItem(
          key,
          JSON.stringify([...ragPrompts, normalizeInput])
        );
      } else {
        sessionStorage.setItem(key, JSON.stringify([normalizeInput]));
      }

      const res = await fetch(`${rag_api_url}/process-query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: normalizeInput,
          source: "dashboard",
          user_id: "09i8u7600s", // This will protect llm spamming for now. All user will be using the same id.
        }),
      });
      if (!res.body) throw new Error("ReadableStream not supported");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      let buffer = "";
      let fullText = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          const existingData =
            JSON.parse(sessionStorage.getItem("rag_data")) || [];

          const updateData = [
            ...existingData,
            fullText.replace(/data:\s*\[DONE\]/g, ""),
          ];

          sessionStorage.setItem("rag_data", JSON.stringify(updateData));
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Split by SSE delimiter
        const parts = buffer.split("\n\n");
        buffer = parts.pop(); // keep incomplete chunk

        for (const part of parts) {
          if (!part.startsWith("data:")) continue;

          const data = part.replace(/^data:\s*/, "");

          if (data === "[DONE]") {
            reader.cancel();
            break;
          }

          if (data === "[TOKEN_LIMIT_EXCEEDED]") {
            alert("Token limit reached");
            reader.cancel();
            break;
          }
          // Add space only if needed
          if (
            fullText.length > 0 &&
            !/\s$/.test(fullText) &&
            /^[\w]/.test(data)
          ) {
            fullText += " ";
          }

          fullText += data;
          setResponse(fullText);
          setTextInput("");
        }
      }
    } catch (err) {
      console.log(
        err instanceof Error ? err.message : "Unknown error occurred"
      );
      setTextInput("");
    } finally {
      setSending(false);
    }
  }

  async function sendFileToApi() {
    setSending(true);
    const formdata = new FormData();
    abortControllerRef.current = new AbortController();

    formdata.append("document", file);

    const requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };

    try {
      const response = await fetch(`${api_url}/upload-document`, {
        ...requestOptions,
        signal: abortControllerRef.current.signal,
      });

      const result = await response.json();

      if (result?.pythonResponse?.file?.filename) {
        setUploaded(true);
        alert("File Uploaded Successfully");
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Upload cancelled");
      } else {
        console.error(error);
      }
    } finally {
      setSending(false);
    }
  }

  React.useEffect(() => {
    if (isUploaded) {
      const submittedId = setTimeout(() => {
        fileInputRef.current.value = "";
        setFile("");
        setUploaded(false);
      }, 3000);

      return () => {
        abortControllerRef.current?.abort();
        clearTimeout(submittedId);
      };
    }
  }, [isUploaded]);

  const override = {
    display: "block",
    borderColor: "text-deepGrey",
  };

  useEffect(() => {
    // Get stored rag data
    const ragData = JSON.parse(sessionStorage.getItem("rag_data")) || [];
    const ragPrompt = JSON.parse(sessionStorage.getItem("rag_prompts")) || [];
    setResponsedata({
      data: ragData,
      prompts: ragPrompt,
    });
  }, [response]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [responseData, response]);

  return (
    <>
      <Helmet>
        <title>Wiingr | RAG</title>
      </Helmet>
      <Layout isRag={true}>
        <div className="bg-[#181818] h-[100vh] w-full flex flex-col">
          <div className="relative w-[100%] md:w-[60%] 2xl:w-[50%] h-full mx-auto flex flex-col">
            <div
              className="w-full flex py-2 tracking-wider justify-center bg-clip-text text-transparent 
  bg-gradient-to-r from-white/60 via-white/10 to-white/60
  bg-[length:200%_200%] animate-gradient-move"
            >
              <p className="font-saira font-light text-xs sm:text-sm uppercase">
                LangChain-Powered RAG Playground
              </p>
            </div>
            <RagChatView
              response={response}
              data={responseData?.data}
              prompts={responseData?.prompts}
              bottomRef={bottomRef}
            />
            <div className=" mb-10 flex justify-center">
              <div className="bg-[rgba(0,0,0,10%)] w-full h-fit rounded-xl">
                <textarea
                  type="text"
                  placeholder="Ask..."
                  row={10}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  className="resize-none w-[95%] mt-3 mb-[4px] border-[0.5px] focus:ring-0 focus:border-none border-none rounded-xl bg-transparent text-sm leading-5 text-white/40 placeholder:text-white/40"
                />
                <div className="w-full flex flex-row justify-between items-center px-3 pb-3">
                  <div className="flex flex-row items-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      // multiple
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleFileChange}
                      className="text-white/20 text-[10px] sm:text-sm file:text-[10px] sm:file:text-sm file:text[8px] block w-full file:mr-2 file:p-[3px] sm:file:py-1 sm:file:px-2 file:rounded-md file:border-0 file:font-light file:bg-linesLighter/5 file:text-white/40 cursor-pointer hover:file:text-white/20 hover:file:bg-linesLighter/0 transition-all duration-500"
                    />
                  </div>
                  {file ? (
                    sending ? (
                      <ClipLoader
                        color="text-lightGrey"
                        loading={sending}
                        cssOverride={override}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                    ) : (
                      <svg
                        onClick={sendFileToApi}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-4 text-white/40 hover:text-white/20 cursor-pointer transition-all duration-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M7.5 7.5h-.75A2.25 2.25 0 0 0 4.5 9.75v7.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-7.5a2.25 2.25 0 0 0-2.25-2.25h-.75m0-3-3-3m0 0-3 3m3-3v11.25m6-2.25h.75a2.25 2.25 0 0 1 2.25 2.25v7.5a2.25 2.25 0 0 1-2.25 2.25h-7.5a2.25 2.25 0 0 1-2.25-2.25v-.75"
                        />
                      </svg>
                    )
                  ) : sending ? (
                    <ClipLoader
                      color="text-lightGrey"
                      loading={sending}
                      cssOverride={override}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                    />
                  ) : (
                    <button
                      disabled={sending || !textInput}
                      onClick={sendQuery}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-4 text-white/40 hover:text-white/20 cursor-pointer transition-all duration-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export default Rag;
